import { ref, watch } from 'vue'

/**
 * Composable for managing user preferences in localStorage
 * Provides a reusable way to persist user settings across sessions
 */

const STORAGE_PREFIX = 'user_pref_'

/**
 * Creates a reactive reference that syncs with localStorage
 * @param {string} key - The preference key (will be prefixed automatically)
 * @param {*} defaultValue - Default value if no stored value exists
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoSave - Whether to automatically save on value change (default: true)
 * @param {number} options.debounce - Debounce time in ms for auto-save (default: 300)
 * @returns {Object} - Object containing the reactive value and utility methods
 */
export function useUserPreference(key, defaultValue, options = {}) {
  const {
    autoSave = true,
    debounce = 300,
  } = options

  const storageKey = STORAGE_PREFIX + key

  // Try to load saved value from localStorage
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored !== null) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.warn(`Failed to load preference "${key}":`, error)
    }
    return defaultValue
  }

  // Create reactive reference with stored or default value
  const value = ref(loadFromStorage())

  // Save value to localStorage
  const save = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(value.value))
    } catch (error) {
      console.warn(`Failed to save preference "${key}":`, error)
    }
  }

  // Clear stored value and reset to default
  const clear = () => {
    try {
      localStorage.removeItem(storageKey)
      value.value = defaultValue
    } catch (error) {
      console.warn(`Failed to clear preference "${key}":`, error)
    }
  }

  // Auto-save when value changes (if enabled)
  if (autoSave) {
    let timeoutId = null
    watch(value, () => {
      if (debounce > 0) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(save, debounce)
      } else {
        save()
      }
    })
  }

  return {
    value,
    save,
    clear,
  }
}

/**
 * Manages multiple user preferences as a group
 * @param {Object} preferences - Object defining preferences with their defaults
 * @returns {Object} - Object containing all preference values and utility methods
 */
export function useUserPreferences(preferences = {}) {
  const prefs = {}
  const methods = {
    save: () => {},
    clear: () => {},
    clearAll: () => {},
  }

  // Create individual preferences
  Object.keys(preferences).forEach(key => {
    const { defaultValue, ...options } = preferences[key]
    prefs[key] = useUserPreference(key, defaultValue, options)
  })

  // Save all preferences
  methods.save = () => {
    Object.values(prefs).forEach(pref => pref.save())
  }

  // Clear all preferences
  methods.clearAll = () => {
    Object.values(prefs).forEach(pref => pref.clear())
  }

  return {
    ...prefs,
    ...methods,
  }
}

/**
 * Clear all user preferences from localStorage
 */
export function clearAllUserPreferences() {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.warn('Failed to clear all user preferences:', error)
  }
}

